import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// HELPERS
// ============================================

const getWorkoutDisplayStatus = (workout) => {
  if (!workout) return { status: 'no_workout', label: 'Sin entrenamiento', color: 'bg-slate-500/20 text-slate-300' };
  if (workout.status === 'completed' || workout.is_finished) return { status: 'finished', label: 'Completado', color: 'bg-emerald-500/20 text-emerald-300' };
  if (workout.status === 'in_progress') return { status: 'in_progress', label: 'En progreso', color: 'bg-amber-500/20 text-amber-300' };
  return { status: 'planned', label: 'Planificado', color: 'bg-blue-500/20 text-blue-300' };
};

const getBookingStatusDisplay = (status) => {
  switch (status) {
    case 'confirmed': return { label: 'Confirmada', color: 'bg-green-500/20 text-green-300' };
    case 'reserved': return { label: 'Reservada', color: 'bg-blue-500/20 text-blue-300' };
    case 'completed': return { label: 'Completada', color: 'bg-emerald-500/20 text-emerald-300' };
    case 'cancelled': return { label: 'Cancelada', color: 'bg-red-500/20 text-red-300' };
    case 'no_show': return { label: 'No asististe', color: 'bg-orange-500/20 text-orange-300' };
    default: return { label: status || 'Pendiente', color: 'bg-slate-500/20 text-slate-300' };
  }
};

const formatDateSpanish = (dateStr) => {
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return dateStr; }
};

const formatShortDate = (dateStr) => {
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  } catch { return dateStr; }
};

const getLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getHoursUntilSession = (booking) => {
  if (!booking?.date || !booking?.start_time) return null;
  try {
    const dt = new Date(`${booking.date}T${booking.start_time}:00`);
    return (dt - new Date()) / (1000 * 60 * 60);
  } catch { return null; }
};


// ============================================
// PROGRESS STRIP — Motivational summary
// ============================================
const ProgressStrip = ({ progressData }) => {
  if (!progressData) return null;

  const { sessions_this_week, sessions_this_month, total_sessions, streak_weeks, volume_trend, volume_change_pct } = progressData;

  const getMotivationalLine = () => {
    if (total_sessions === 0) return 'Tu primer entrenamiento te espera';
    if (volume_trend === 'up' && volume_change_pct > 5) return 'Vas por muy buen camino, sigue asi';
    if (volume_trend === 'up') return 'Estas mejorando sesion tras sesion';
    if (streak_weeks >= 4) return `${streak_weeks} semanas sin fallar, eso es constancia`;
    if (sessions_this_week >= 3) return 'Semana intensa, gran trabajo';
    if (sessions_this_week >= 1) return 'Ya arrancaste la semana, no pares';
    if (sessions_this_month >= 1) return 'Cada sesion cuenta, vamos a por mas';
    return 'Tu cuerpo te lo va a agradecer';
  };

  return (
    <div className="rounded-2xl bg-gradient-to-r from-slate-800/80 to-slate-800/40 border border-slate-700/40 p-5 mb-6" data-testid="progress-strip">
      {/* Motivational headline */}
      <p className="text-blue-200 text-lg font-medium mb-4" data-testid="motivational-message">
        {getMotivationalLine()}
      </p>

      {/* Stats row */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-white" data-testid="sessions-this-week">{sessions_this_week}</span>
          <span className="text-slate-400 text-sm">sesiones esta semana</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-white" data-testid="sessions-this-month">{sessions_this_month}</span>
          <span className="text-slate-400 text-sm">este mes</span>
        </div>
        {streak_weeks > 1 && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-emerald-400" data-testid="streak-weeks">{streak_weeks}</span>
            <span className="text-slate-400 text-sm">semanas consecutivas</span>
          </div>
        )}
        {total_sessions > 0 && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-purple-300" data-testid="total-sessions">{total_sessions}</span>
            <span className="text-slate-400 text-sm">sesiones totales</span>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// TODAY'S HERO CARD — "Tu sesion de hoy"
// ============================================
const TodayHeroCard = ({ booking, workout, onStartWorkout, onViewResults, progressData }) => {
  const statusInfo = getWorkoutDisplayStatus(workout);

  // Performance comparison message
  const getPerformanceLine = () => {
    if (!progressData?.last_workout || !progressData?.previous_workout) return null;
    if (statusInfo.status !== 'finished') return null;

    const { volume_trend, volume_change_pct } = progressData;
    if (volume_trend === 'up' && volume_change_pct > 0) {
      return `Mejor que tu sesion anterior (+${volume_change_pct}% volumen)`;
    }
    if (volume_trend === 'stable') {
      return 'Mantuviste el nivel, buen trabajo';
    }
    return null;
  };

  const getButtonConfig = () => {
    switch (statusInfo.status) {
      case 'planned': return { text: 'Comenzar entrenamiento', action: () => onStartWorkout(booking, workout), cls: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white' };
      case 'in_progress': return { text: 'Continuar entrenamiento', action: () => onStartWorkout(booking, workout), cls: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white' };
      case 'finished': return { text: 'Resumen de tu sesion', action: () => onViewResults(booking, workout), cls: 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white' };
      default: return null;
    }
  };

  const btn = getButtonConfig();
  const perfLine = getPerformanceLine();

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 border border-blue-500/30" data-testid="today-hero-card">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10" />
      <div className="relative p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-blue-400 text-sm font-medium uppercase tracking-wide mb-1" data-testid="today-label">Tu sesion de hoy</p>
            <h2 className="text-white text-2xl font-bold" data-testid="today-time">Hoy {booking.start_time}</h2>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`} data-testid="today-status">{statusInfo.label}</span>
        </div>

        {/* Performance feedback */}
        {perfLine && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="performance-feedback">
            <p className="text-emerald-300 text-sm font-medium">{perfLine}</p>
          </div>
        )}

        {btn && (
          <button
            onClick={btn.action}
            className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] ${btn.cls}`}
            data-testid="today-workout-action-btn"
          >
            <span>{btn.text}</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// UPCOMING SESSION ITEM
// ============================================
const UpcomingSessionItem = ({ booking, workout, cancellationWindowHours, onViewSession, onCancelSession, onRescheduleSession, isBookingFlex }) => {
  const hoursUntil = getHoursUntilSession(booking);
  const canCancel = hoursUntil !== null && hoursUntil > cancellationWindowHours;
  const canReschedule = hoursUntil !== null && hoursUntil > cancellationWindowHours;
  const isLateWindow = hoursUntil !== null && hoursUntil > 0 && hoursUntil <= cancellationWindowHours;
  const statusInfo = getWorkoutDisplayStatus(workout);
  const bookingStatus = getBookingStatusDisplay(booking.status);

  const dayName = (() => {
    try {
      const d = new Date(booking.date + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long' });
      return d.charAt(0).toUpperCase() + d.slice(1);
    } catch { return booking.date; }
  })();

  return (
    <div className="py-4 px-4 border-b border-slate-700/50 last:border-0" data-testid={`booking-${booking.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white font-medium">{dayName}, {formatShortDate(booking.date)}</p>
            <span className="text-blue-300 font-semibold">{booking.start_time} - {booking.end_time}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded ${bookingStatus.color}`}>{bookingStatus.label}</span>
            {workout && <span className={`text-xs px-2 py-0.5 rounded ${statusInfo.color}`}>{statusInfo.label}</span>}
            {isLateWindow && (
              <span className="text-xs text-amber-400">
                Quedan menos de {cancellationWindowHours}h
              </span>
            )}
          </div>
        </div>

        {isBookingFlex && booking.status !== 'cancelled' && (
          <div className="flex gap-2 items-center">
            {workout && (
              <button onClick={() => onViewSession(booking, workout)} className="text-sm text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-500/10 transition" data-testid={`view-booking-${booking.id}`}>
                Ver
              </button>
            )}
            {canReschedule ? (
              <button onClick={() => onRescheduleSession(booking)} className="text-sm text-purple-400 hover:text-purple-300 px-3 py-1.5 rounded-lg hover:bg-purple-500/10 transition" data-testid={`reschedule-booking-${booking.id}`}>
                Cambiar hora
              </button>
            ) : isLateWindow ? (
              <span className="text-sm text-slate-500 px-3 py-1.5" title={`Necesitas ${cancellationWindowHours}h de antelacion`}>Cambiar hora</span>
            ) : null}
            {canCancel ? (
              <button onClick={() => onCancelSession(booking)} className="text-sm text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition" data-testid={`cancel-booking-${booking.id}`}>
                Cancelar
              </button>
            ) : isLateWindow ? (
              <div className="relative group">
                <button onClick={() => onCancelSession(booking)} className="text-sm text-red-400/50 px-3 py-1.5 rounded-lg" data-testid={`late-cancel-booking-${booking.id}`}>
                  Cancelar
                </button>
                <div className="absolute right-0 top-full mt-1 bg-slate-800 text-amber-300 text-xs p-2 rounded-lg shadow-xl border border-slate-700 whitespace-nowrap z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Cancelacion tardia (menos de {cancellationWindowHours}h)
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// PROGRESS SESSION ITEM (replaces PastBookingItem)
// Shows volume comparison and evolution
// ============================================
const ProgressSessionItem = ({ booking, workout, onViewResults, historyEntry }) => {
  const isFlexible = !booking;
  const bookingStatus = getBookingStatusDisplay(booking?.status);
  const totalVolume = workout?.total_volume != null
    ? workout.total_volume
    : (workout ? (workout.exercises || []).reduce((sum, ex) =>
        sum + (ex.actual_sets || []).reduce((s, set) => s + ((set.weight || 0) * (set.reps || 0)), 0), 0
      ) : 0);
  const exerciseCount = workout?.exercise_count != null ? workout.exercise_count : (workout?.exercises?.length || 0);
  const itemDate = booking?.date || workout?.date || '';
  const statusValue = isFlexible ? (workout?.is_finished ? 'completed' : 'scheduled') : booking?.status;

  // Volume change indicator from history data
  const changeInfo = (() => {
    if (!historyEntry || historyEntry.volume_change_pct === undefined) return null;
    const pct = historyEntry.volume_change_pct;
    if (pct > 2) return { label: `+${pct}%`, cls: 'text-emerald-400', icon: '\u2197' };
    if (pct < -2) return { label: `${pct}%`, cls: 'text-red-400', icon: '\u2198' };
    return { label: 'igual', cls: 'text-slate-400', icon: '\u2192' };
  })();

  const sessionLabel = (() => {
    if (statusValue === 'cancelled') return 'Sesion cancelada';
    if (statusValue === 'no_show') return 'No asististe';
    if (!workout) return 'Sin entrenamiento';
    if (totalVolume > 0) return `${exerciseCount} ej. · ${totalVolume.toLocaleString()} kg`;
    return `${exerciseCount} ejercicios`;
  })();

  return (
    <div
      onClick={() => workout?.is_finished && onViewResults(booking, workout)}
      className={`flex items-center justify-between py-3 px-4 rounded-lg bg-slate-800/30 transition ${
        workout?.is_finished ? 'hover:bg-slate-800/50 cursor-pointer group' : ''
      }`}
      data-testid={`progress-session-${booking?.id || workout?.id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* Status dot */}
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
          isFlexible ? 'bg-amber-400' :
          statusValue === 'completed' ? 'bg-emerald-400' :
          statusValue === 'cancelled' ? 'bg-red-400' : 'bg-slate-500'
        }`} />

        <div className="min-w-0">
          <p className={`font-medium truncate flex items-center gap-1.5 ${workout?.is_finished ? 'text-white group-hover:text-blue-300' : 'text-slate-400'} transition`}>
            {isFlexible && <span title="Rutina libre" className="flex-shrink-0">🏠</span>}
            {formatShortDate(itemDate)}{!isFlexible && booking?.start_time ? ` · ${booking.start_time}` : ''}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-slate-400 text-sm truncate">
              {isFlexible && workout?.template_name ? `${workout.template_name} · ` : ''}{sessionLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Volume change badge */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {changeInfo && (
          <span className={`text-xs font-semibold ${changeInfo.cls}`} data-testid={`volume-change-${booking?.id || workout?.id}`}>
            {changeInfo.icon} {changeInfo.label}
          </span>
        )}
        {workout?.is_finished && (
          <span className="text-slate-500 group-hover:text-blue-400 transition text-sm">{'\u2192'}</span>
        )}
      </div>
    </div>
  );
};


// ============================================
// RESCHEDULE MODAL
// ============================================
const RescheduleModal = ({ booking, onConfirm, onClose }) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/timeslots/available`, { headers: { Authorization: `Bearer ${token}` } });
        const now = new Date();
        const filtered = (res.data || []).filter(s => {
          const slotDt = new Date(`${s.date}T${s.start_time}:00`);
          return slotDt > now && s.id !== booking.slot_id;
        });
        setAvailableSlots(filtered);
      } catch (err) {
        console.error('Error fetching slots:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSlots();
  }, [booking.slot_id]);

  const slotsByDate = useMemo(() => {
    const groups = {};
    availableSlots.forEach(s => {
      if (!groups[s.date]) groups[s.date] = [];
      groups[s.date].push(s);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [availableSlots]);

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    await onConfirm(booking.id, selectedSlot.id);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-white">Cambiar hora de sesion</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-xl">{'\u00D7'}</button>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Sesion actual: <span className="text-blue-300">{formatDateSpanish(booking.date)} {'\u00B7'} {booking.start_time}</span>
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Cargando horarios disponibles...</div>
          ) : slotsByDate.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No hay horarios disponibles</div>
          ) : (
            <div className="space-y-4">
              {slotsByDate.map(([date, slots]) => (
                <div key={date}>
                  <p className="text-sm font-semibold text-slate-300 mb-2 capitalize">{formatDateSpanish(date)}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map(slot => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={`py-2 px-3 rounded-lg text-sm font-medium transition ${
                          selectedSlot?.id === slot.id
                            ? 'bg-blue-500 text-white border-2 border-blue-400'
                            : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-700'
                        }`}
                        data-testid={`slot-option-${slot.id}`}
                      >
                        {slot.start_time} - {slot.end_time}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-700 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600 transition font-medium">
            Volver
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedSlot || submitting}
            className={`flex-1 py-3 rounded-xl font-medium transition ${
              selectedSlot && !submitting
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="confirm-reschedule-btn"
          >
            {submitting ? 'Procesando...' : 'Confirmar cambio'}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================
// MAIN COMPONENT
// ============================================
const ClientMySessionsTab = ({
  bookings,
  workouts,
  onStartWorkout,
  onViewResults,
  onRefresh,
  userId,
  isBookingFlex = true,
  progressData = null
}) => {
  const today = getLocalDateString(new Date());
  const [viewTab, setViewTab] = useState('upcoming');
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [clientBookings, setClientBookings] = useState(null);
  const [cancellationWindowHours, setCancellationWindowHours] = useState(24);

  const fetchClientBookings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/bookings/client/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientBookings(res.data);
      setCancellationWindowHours(res.data.cancellation_window_hours || 24);
    } catch (err) {
      console.error('Error fetching client bookings:', err);
    }
  }, []);

  useEffect(() => {
    fetchClientBookings();
  }, [fetchClientBookings]);

  const allBookings = clientBookings
    ? [...(clientBookings.upcoming || []), ...(clientBookings.past || [])]
    : bookings;

  const getWorkoutForBooking = (booking) => workouts.find(w => w.booking_id === booking.id);

  // Build a map from date -> history entry for volume comparison
  const historyMap = useMemo(() => {
    if (!progressData?.history) return {};
    const map = {};
    progressData.history.forEach(h => { map[h.date] = h; });
    return map;
  }, [progressData]);

  // TODAY'S SESSION
  const todaySession = useMemo(() => {
    const b = allBookings.find(b => b.date === today && b.status !== 'cancelled');
    if (!b) return null;
    return { booking: b, workout: getWorkoutForBooking(b) };
  }, [allBookings, workouts, today]);

  // UPCOMING (excluding today)
  const upcomingSessions = useMemo(() => {
    return allBookings
      .filter(b => b.date >= today && b.status !== 'cancelled' && !(b.date === today && todaySession))
      .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''))
      .map(b => ({ booking: b, workout: getWorkoutForBooking(b) }));
  }, [allBookings, workouts, today, todaySession]);

  // NEXT SESSION for empty state
  const nextSession = useMemo(() => {
    const future = allBookings
      .filter(b => b.date >= today && b.status !== 'cancelled')
      .sort((a, b) => a.date.localeCompare(b.date));
    const next = future[0]?.date === today ? future[1] : future[0];
    if (!next) return null;
    return { booking: next, workout: getWorkoutForBooking(next) };
  }, [allBookings, workouts, today]);

  // PAST
  const pastSessions = useMemo(() => {
    const booked = allBookings
      .filter(b => b.date < today || b.status === 'completed' || b.status === 'no_show' || b.status === 'cancelled')
      .map(b => ({ booking: b, workout: getWorkoutForBooking(b), date: b.date, start_time: b.start_time || '' }));
    // Completed free routines have no booking — surface them in the unified
    // history so the client's calendar shows everything they actually did.
    const flexibleDone = (workouts || [])
      .filter(w => w.mode === 'flexible' && (w.is_finished || w.status === 'completed'))
      .map(w => ({ booking: null, workout: w, date: w.date || '', start_time: '' }));
    return [...booked, ...flexibleDone]
      .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.start_time || '').localeCompare(a.start_time || ''));
  }, [allBookings, workouts, today]);

  // Cancel
  const handleCancelSession = async (booking) => {
    const hoursUntil = getHoursUntilSession(booking);
    const isLate = hoursUntil !== null && hoursUntil <= cancellationWindowHours;

    let msg = 'Quieres cancelar esta sesion?';
    if (isLate) {
      msg = `Quedan menos de ${cancellationWindowHours}h para tu sesion.\nSe registrara como cancelacion tardia.\n\nContinuar?`;
    }
    if (!window.confirm(msg)) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/bookings/${booking.id}/client-cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.late_cancellation) {
        alert('Sesion cancelada (cancelacion tardia registrada)');
      } else {
        alert('Sesion cancelada correctamente');
      }
      fetchClientBookings();
      onRefresh();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'No se pudo cancelar'));
    }
  };

  // Reschedule
  const handleRescheduleConfirm = async (bookingId, newSlotId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/bookings/${bookingId}/reschedule-to/${newSlotId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      alert(`Sesion movida a ${formatDateSpanish(d.new_date)} a las ${d.new_start_time}`);
      setRescheduleBooking(null);
      fetchClientBookings();
      onRefresh();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'No se pudo reprogramar'));
    }
  };

  return (
    <div className="space-y-6" data-testid="client-sessions-tab">

      {/* ===== PROGRESS STRIP ===== */}
      <ProgressStrip progressData={progressData} />

      {/* ===== TODAY'S HERO ===== */}
      <section>
        {todaySession ? (
          <TodayHeroCard
            booking={todaySession.booking}
            workout={todaySession.workout}
            onStartWorkout={onStartWorkout}
            onViewResults={onViewResults}
            progressData={progressData}
          />
        ) : (
          <div className="rounded-2xl bg-slate-800/50 border border-slate-700/50 p-6 text-center" data-testid="no-today-session">
            <h3 className="text-white text-xl font-semibold mb-2">Hoy descansas</h3>
            {nextSession ? (
              <p className="text-slate-400">
                Tu proxima sesion es el <span className="text-blue-300 font-medium">{formatDateSpanish(nextSession.booking.date)} a las {nextSession.booking.start_time}</span>
              </p>
            ) : (
              <p className="text-slate-400">No tienes sesiones programadas por ahora</p>
            )}
            {isBookingFlex && (
              <button
                onClick={() => {
                  const bookBtn = document.querySelector('[data-testid="book-session-btn"]');
                  if (bookBtn) bookBtn.click();
                }}
                className="mt-4 px-6 py-2.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-xl hover:bg-blue-500/30 transition font-medium"
                data-testid="empty-state-book-btn"
              >
                Reservar sesion
              </button>
            )}
          </div>
        )}
      </section>

      {/* ===== TAB TOGGLE: UPCOMING / PROGRESS ===== */}
      <div className="flex gap-2 border-b border-slate-700/50 pb-2">
        <button
          onClick={() => setViewTab('upcoming')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${viewTab === 'upcoming' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-slate-400 hover:text-slate-300'}`}
          data-testid="tab-upcoming"
        >
          Proximas sesiones ({upcomingSessions.length})
        </button>
        <button
          onClick={() => setViewTab('past')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${viewTab === 'past' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 hover:text-slate-300'}`}
          data-testid="tab-past"
        >
          Tu progreso ({pastSessions.length})
        </button>
      </div>

      {/* ===== UPCOMING SESSIONS ===== */}
      {viewTab === 'upcoming' && (
        <section>
          {upcomingSessions.length > 0 ? (
            <div className="bg-slate-800/30 rounded-xl border border-slate-700/50">
              {upcomingSessions.map(({ booking, workout }) => (
                <UpcomingSessionItem
                  key={booking.id}
                  booking={booking}
                  workout={workout}
                  cancellationWindowHours={cancellationWindowHours}
                  onViewSession={(b, w) => w ? onViewResults(b, w) : null}
                  onCancelSession={handleCancelSession}
                  onRescheduleSession={(b) => setRescheduleBooking(b)}
                  isBookingFlex={isBookingFlex}
                />
              ))}
            </div>
          ) : (
            <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-6 text-center">
              <p className="text-slate-400">No tienes sesiones programadas por ahora</p>
            </div>
          )}

          <div className="mt-3 px-1">
            <p className="text-slate-500 text-xs">
              Puedes cancelar o cambiar sesiones con al menos {cancellationWindowHours}h de antelacion.
            </p>
          </div>
        </section>
      )}

      {/* ===== TU PROGRESO (formerly History) ===== */}
      {viewTab === 'past' && (
        <section>
          {/* Volume trend highlight */}
          {progressData && progressData.total_sessions > 1 && (
            <div className="mb-4 p-4 rounded-xl bg-slate-800/40 border border-slate-700/40" data-testid="progress-highlight">
              <p className="text-slate-300 text-sm">
                {progressData.volume_trend === 'up'
                  ? `Tu volumen de entrenamiento esta subiendo (+${progressData.volume_change_pct}%). Sigue asi.`
                  : progressData.volume_trend === 'down'
                    ? 'Tu volumen ha bajado un poco respecto a la sesion anterior. Cada dia es una oportunidad nueva.'
                    : 'Estas manteniendo un ritmo constante. La constancia es clave.'
                }
              </p>
            </div>
          )}

          {pastSessions.length > 0 ? (
            <div className="space-y-2">
              {pastSessions.map(({ booking, workout }) => (
                <ProgressSessionItem
                  key={booking?.id || workout?.id}
                  booking={booking}
                  workout={workout}
                  onViewResults={onViewResults}
                  historyEntry={historyMap[booking?.date || workout?.date]}
                />
              ))}
            </div>
          ) : (
            <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-6 text-center">
              <p className="text-slate-400">Aun no tienes historial. Tu progreso aparecera aqui despues de tu primera sesion.</p>
            </div>
          )}
        </section>
      )}

      {/* ===== RESCHEDULE MODAL ===== */}
      {rescheduleBooking && (
        <RescheduleModal
          booking={rescheduleBooking}
          onConfirm={handleRescheduleConfirm}
          onClose={() => setRescheduleBooking(null)}
        />
      )}
    </div>
  );
};

export default ClientMySessionsTab;
